import * as XLSX from "xlsx";
import moment from "moment";

// Required by the external student import system: exact header text/order,
// worksheet literally named "Students", data starting on row 2.
const WORKSHEET_NAME = "Students";
const VALID_PAYMENT_TYPES = ["Cash", "Credit Card", "Check"];

const COLUMNS = [
  { header: "First Name", key: "fName" },
  { header: "Middle Name", key: "mName" },
  { header: "Last Name", key: "lName" },
  { header: "DLN", key: "permitNo" },
  { header: "Date of Birth", key: "dob" },
  { header: "Student Email", key: "emailAddress" },
  { header: "Total Program Cost", key: "totalProgram" },
  { header: "Payment Type", key: "paymentType" },
  { header: "Initial Payment", key: "initalPayment" },
];

function formatMoney(value) {
  const num = Number(value);
  return Number.isFinite(num) ? num.toFixed(2) : "";
}

function formatDOB(value) {
  const parsed = moment(value, ["YYYY-MM-DD", "M/D/YYYY", "M/D/YY"], true);
  return parsed.isValid() ? parsed.format("MM/DD/YYYY") : (value || "");
}

function formatDLN(value) {
  return (value || "").toString().toUpperCase();
}

/**
 * Maps raw student records to the required 9-column shape and collects
 * validation warnings (missing/invalid fields) without dropping any rows.
 */
export function buildStudentImportRows(students) {
  const warnings = [];

  const rows = students.map((student, index) => {
    const who = `${student.fName || ""} ${student.lName || ""}`.trim() || `Row ${index + 2}`;

    if (!student.fName) warnings.push(`${who}: missing First Name`);
    if (!student.lName) warnings.push(`${who}: missing Last Name`);
    if (!student.permitNo) warnings.push(`${who}: missing DLN`);
    if (!student.emailAddress) warnings.push(`${who}: missing Student Email`);
    if (!formatDOB(student.dob)) warnings.push(`${who}: missing/invalid Date of Birth`);
    if (!VALID_PAYMENT_TYPES.includes(student.paymentType)) {
      warnings.push(`${who}: Payment Type must be Cash, Credit Card, or Check (got "${student.paymentType || ""}")`);
    }

    return COLUMNS.map(({ key }) => {
      if (key === "dob") return formatDOB(student.dob);
      if (key === "permitNo") return formatDLN(student.permitNo);
      if (key === "totalProgram" || key === "initalPayment") return formatMoney(student[key]);
      return student[key] || "";
    });
  });

  return { rows, warnings };
}

/**
 * Builds and downloads the .xlsx file. Always produces the file (even with
 * validation warnings) so staff aren't blocked; callers should surface the
 * returned warnings to the user.
 */
export function downloadStudentImportXlsx(students, filename) {
  const { rows, warnings } = buildStudentImportRows(students);

  const worksheet = XLSX.utils.aoa_to_sheet([
    COLUMNS.map((c) => c.header),
    ...rows,
  ]);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, WORKSHEET_NAME);

  XLSX.writeFile(workbook, filename.endsWith(".xlsx") ? filename : `${filename}.xlsx`);

  return { warnings };
}
